import React, {useEffect, useState} from 'react';
import AuthenticatedLayout from '../authenticatedLayout/AuthenticatedLayout';
import {useAuth} from '../AuthContext';
import {BASE_URL} from '../../Constants';
import './HistoryPage.css';
import '../../App.css'

const HistoryPage = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [clientHistory, setClientHistory] = useState([]);
    const [filteredHistory, setFilteredHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const {isAuthenticated} = useAuth();

    useEffect(() => {
        // Fetch client history data from the backend
        const fetchClientHistory = async () => {
            try {
                setLoading(true);

                // Get authorization token from localStorage
                const token = localStorage.getItem('token');

                if (!token) {
                    throw new Error('No authentication token found');
                }

                // Get current date in YYYY-MM-DD format
                const today = new Date();
                const oneYearAgo = new Date();
                oneYearAgo.setFullYear(today.getFullYear() - 1);

                // Fetch events between dates
                // Using the /event endpoint from EventController in the backend
                const response = await fetch(`${BASE_URL}/event/all`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();

                // Transform and prepare data for display
                const historyData = data.map(event => {
                    return {
                        id: event.id,
                        date: event.scheduledDate,
                        name: event.customerEntity?.lastName + ' ' + event.customerEntity?.firstName || event.name || 'N/A',
                        phone: event.customerEntity?.phone1 || 'N/A',
                        mobilierType: event.customerEntity?.furnitureType || 'N/A',
                        eventType: event.eventType || 'N/A',
                        customerId: event.customerEntity?.id || null
                    };
                });

                setClientHistory(historyData);
                setFilteredHistory(historyData);
                setError(null);
            } catch (err) {
                console.error('Error fetching client history:', err);
                setError('Nu s-au putut încărca datele istoricului. Vă rugăm încercați din nou.');
            } finally {
                setLoading(false);
            }
        };

        if (isAuthenticated) {
            fetchClientHistory();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        // Filter the client history based on search term
        if (searchTerm.trim() === '') {
            setFilteredHistory(clientHistory);
        } else {
            const lowercasedTerm = searchTerm.toLowerCase();
            const filtered = clientHistory.filter(item => {
                // Format date for search
                const formattedDate = item.date ? formatDate(item.date).toLowerCase() : '';

                return (
                    (item.name && item.name.toLowerCase().includes(lowercasedTerm)) ||
                    (item.phone && item.phone.toLowerCase().includes(lowercasedTerm)) ||
                    (item.mobilierType && item.mobilierType.toLowerCase().includes(lowercasedTerm)) ||
                    (item.eventType && item.eventType.toLowerCase().includes(lowercasedTerm)) || // Added eventType search
                    (item.date && item.date.includes(lowercasedTerm)) || // Original date search (YYYY-MM-DD)
                    (formattedDate && formattedDate.includes(lowercasedTerm)) // Formatted date search (DD.MM.YYYY)
                );
            });
            setFilteredHistory(filtered);
        }
    }, [searchTerm, clientHistory]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // Format date from YYYY-MM-DD to DD.MM.YYYY
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const parts = dateString.split('-');
        return `${parts[2]}.${parts[1]}.${parts[0]}`;
    };

    return (
        <AuthenticatedLayout>
            <div className="istoric-container">
                <div className="header-container">
                    <h1 className="page-title">Istoric</h1>
                </div>
                <div className="search-container">
                    <div className="search-input-container">
                        <input
                            type="text"
                            placeholder="Caută lucrare"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className="search-input"
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="loading-message">Se încarcă...</div>
                ) : error ? (
                    <div className="error-message">{error}</div>
                ) : (
                    <div className="istoric-content">
                        {filteredHistory.length > 0 ? (
                            <div className="istoric-list">
                                {filteredHistory.map((item, index) => (
                                    <div
                                        key={item.id || index}
                                        className="istoric-item"
                                    >
                                        <div className="istoric-item-numbering">{index + 1}.</div>
                                        <div className="istoric-item-main">
                                            <div className="istoric-item-row">
                                                <div className="istoric-item-name">{item.name}</div>
                                                <div className="istoric-item-phone">
                                                    <span className="phone-label">Telefon:</span> {item.phone}
                                                </div>
                                            </div>
                                            <div className="istoric-item-row second-row">
                                                <div className="istoric-item-type">
                                                    <span
                                                        className="type-label">Tip mobilier:</span> {item.mobilierType}
                                                </div>
                                                <div className="istoric-item-type">
                                                    <span className="type-label">Tip eveniment:</span> {item.eventType}
                                                </div>
                                                <div className="istoric-item-date">
                                                    <span className="date-label">Data:</span> {formatDate(item.date)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no-results">Nu s-au găsit rezultate pentru căutarea dumneavoastră.</div>
                        )}
                    </div>
                )}
            </div>
        </AuthenticatedLayout>
    );
};

export default HistoryPage;